import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { formatInTimeZone } from "@/lib/timezone";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowClockwise, SlidersHorizontal } from "@phosphor-icons/react";

const nn = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const PREF_KEY = "wa_live_stats_opts";

const DEFAULT_OPTS = {
  refreshSec: 5,
  viewMore: true,
  viewUserGroup: false,
  showServerInfo: false,
  hideWaitingCalls: false,
  showInGroupStats: true,
  showPhones: false,
  showMonitors: false,
  showCustInfo: true,
  summary: false,
};

const LEGEND = [
  { color: "#84cc16", label: "Agent chatting" },
  { color: "#f97316", label: "Agent in email" },
  { color: "#22c55e", label: "Agent waiting for call" },
  { color: "#3b82f6", label: "Agent waiting for call > 1 minute" },
  { color: "#1d4ed8", label: "Agent waiting for call > 5 minutes" },
  { color: "#fb923c", label: "Agent on call > 10 seconds" },
  { color: "#a855f7", label: "Agent on call > 1 minute" },
  { color: "#d946ef", label: "Agent on call > 5 minutes" },
  { color: "#d6b48a", label: "Agent Paused > 10 seconds" },
  { color: "#b8956a", label: "Agent Paused > 1 minute" },
  { color: "#8b6914", label: "Agent Paused > 5 minutes" },
  { color: "#6b4f1d", label: "Agent Paused > 10 minutes" },
  { color: "#4a3728", label: "Agent Paused > 15 minutes" },
  { color: "#44403c", label: "Agent on 3-WAY > 10 seconds" },
  { color: "#111827", label: "Agent on a dead call" },
];

const fmtDur = (s) => {
  if (s === null || s === undefined) return "—";
  const sec = Math.max(0, Math.floor(s));
  return `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
};

const loadOpts = () => {
  try {
    const raw = sessionStorage.getItem(PREF_KEY);
    if (!raw) return { ...DEFAULT_OPTS };
    return { ...DEFAULT_OPTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_OPTS };
  }
};

const agentBarColor = (row) => {
  const key = row.status_key;
  const sec = Number(row.elapsed_sec) || 0;
  if (key === "dead") return "#111827";
  if (key === "dispo") return "#44403c";
  if (key === "waiting") {
    if (sec > 300) return "#1d4ed8";
    if (sec > 60) return "#3b82f6";
    return "#22c55e";
  }
  if (key === "incall" || key === "dialing") {
    if (sec > 300) return "#d946ef";
    if (sec > 60) return "#a855f7";
    if (sec > 10) return "#fb923c";
    return "#fb923c";
  }
  if (sec > 900) return "#4a3728";
  if (sec > 600) return "#6b4f1d";
  if (sec > 300) return "#8b6914";
  if (sec > 60) return "#b8956a";
  if (sec > 10) return "#d6b48a";
  return "#d6b48a";
};

const Kpi = ({ label, value, warn, title }) => (
  <div
    className="rounded-lg border border-border bg-card px-3 py-2"
    data-testid={`live-stats-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    title={title || undefined}
  >
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
    <p className={`mt-0.5 text-sm font-semibold tabular-nums ${warn ? "text-red-600" : ""}`}>{nn(value)}</p>
  </div>
);

export default function LiveStats() {
  const { user } = useAuth();
  const [opts, setOpts] = useState(loadOpts);
  const [campaignId, setCampaignId] = useState("");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const first = useRef(true);
  const accountTz = board?.timezone || user?.timezone || "UTC";

  const saveOpts = (next) => {
    setOpts(next);
    try { sessionStorage.setItem(PREF_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/live-stats", {
        params: campaignId ? { campaign_id: campaignId } : {},
      });
      setBoard(data);
    } catch {
      /* keep last snapshot */
    } finally {
      if (first.current) { setLoading(false); first.current = false; }
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const ms = Math.max(2, Number(opts.refreshSec) || 5) * 1000;
    const t = setInterval(load, ms);
    return () => clearInterval(t);
  }, [load, opts.refreshSec]);

  const kpis = board?.kpis || {};
  const counts = board?.agent_counts || {};
  const waiting = board?.waiting || { count: 0, calls: [] };
  const agents = useMemo(() => board?.agents || [], [board]);
  const clock = kpis.time ? formatInTimeZone(kpis.time, accountTz) : "—";

  const toggle = (key) => saveOpts({ ...opts, [key]: !opts[key] });

  return (
    <div data-testid="live-stats-page">
      <PageHeader
        title="Live Stats"
        subtitle={`Real-Time Main Report — campaign, hopper, drop, and agent floor (${accountTz})`}
      >
        <span
          className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.1)] px-3 py-1.5 text-sm font-medium text-[hsl(var(--success))]"
          data-testid="live-stats-live-badge"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--success))]" />
          </span>
          {counts.logged_in || 0} agents
        </span>
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="live-stats-toolbar">
        <div className="space-y-1 min-w-[220px]">
          <Label className="text-xs">Campaign</Label>
          <select
            data-testid="live-stats-campaign"
            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">ALL-ACTIVE</option>
            {(board?.campaigns || []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.status !== "active" ? ` (${c.status})` : ""}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Refresh</Label>
          <select
            data-testid="live-stats-refresh"
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={opts.refreshSec}
            onChange={(e) => saveOpts({ ...opts, refreshSec: Number(e.target.value) })}
          >
            {[2, 4, 5, 10, 15, 30].map((n) => (
              <option key={n} value={n}>{n} seconds</option>
            ))}
          </select>
        </div>
        <Button size="sm" className="rounded-lg" onClick={load} data-testid="live-stats-reload">
          <ArrowClockwise className="w-4 h-4 mr-1.5" /> Reload now
        </Button>
        <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setOptionsOpen(true)} data-testid="live-stats-options">
          <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Display options
        </Button>
        <Button
          size="sm"
          variant={opts.summary ? "default" : "outline"}
          className="rounded-lg"
          onClick={() => toggle("summary")}
          data-testid="live-stats-summary"
        >
          Summary
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="live-stats-toggles">
        {[
          ["viewMore", "View more"],
          ["viewUserGroup", "User group"],
          ["showServerInfo", "Server info"],
          ["hideWaitingCalls", "Hide waiting calls"],
          ["showInGroupStats", "In-group stats"],
          ["showPhones", "Phones"],
          ["showMonitors", "Monitors"],
          ["showCustInfo", "Cust info"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`live-stats-toggle-${key}`}
            onClick={() => toggle(key)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
              opts[key] ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && !board ? (
        <p className="text-sm text-muted-foreground">Loading live stats…</p>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2" data-testid="live-stats-kpis">
            <Kpi label="Dial level" value={kpis.dial_level ?? "—"} />
            {opts.viewMore && <Kpi label="Trunk short/fill" value={`${kpis.trunk_short ?? 0}/${kpis.trunk_fill ?? 0}`} />}
            <Kpi label="Filter" value={kpis.filter} />
            <Kpi label="Time" value={clock} />
            <Kpi label="Dialable leads" value={kpis.dialable_leads} />
            <Kpi label="Calls today" value={kpis.calls_today} />
            <Kpi label="Avg agents" value={kpis.avg_agents} />
            <Kpi label="Dial method" value={kpis.dial_method} />
            {opts.viewMore && (
              <Kpi label="Hopper (min/auto)" value={`${nn(kpis.hopper_min)} / ${nn(kpis.hopper_auto)}`} />
            )}
            <Kpi
              label="Abandon / Answered"
              value={`${kpis.dropped ?? kpis.abandoned ?? 0} / ${kpis.answered ?? 0}`}
              warn={(kpis.dropped || kpis.abandoned || 0) > 0}
              title="Queue abandon + DROP/PDROP (+ legacy Abandoned). Not no-answer and not call-level technical drop ratio."
            />
            <Kpi
              label="No-answer"
              value={kpis.no_answer ?? 0}
              title="No-answer / missed attempts — separate from abandon/DROP."
            />
            {opts.viewMore && <Kpi label="DL Diff" value={kpis.dl_diff ?? "—"} />}
            <Kpi label="Statuses" value={(kpis.statuses || []).join(", ") || "—"} />
            <Kpi label="Leads in hopper" value={kpis.leads_in_hopper} />
            <Kpi
              label="Abandon percent"
              value={`${kpis.dropped_percent ?? 0}%`}
              warn={(kpis.dropped_percent || 0) >= 5}
              title="(Abandon÷(Abandon+Answered))×100. Distinct from campaign technical Drop Ratio."
            />
            {opts.viewMore && <Kpi label="Diff" value={kpis.diff ?? "—"} />}
            {opts.viewMore && <Kpi label="Order" value={kpis.order} />}
          </div>

          {!opts.hideWaitingCalls && (
            <div
              className={`mb-5 rounded-xl border px-4 py-3 text-sm font-semibold ${
                waiting.count ? "border-amber-300 bg-amber-50 text-amber-900" : "border-border bg-card text-muted-foreground"
              }`}
              data-testid="live-stats-waiting"
            >
              {waiting.count
                ? `${waiting.count} live call${waiting.count === 1 ? "" : "s"} waiting`
                : "No live calls waiting"}
              {waiting.count > 0 && (
                <div className="mt-2 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Phone</TableHead>
                        {opts.showCustInfo ? <TableHead>Customer</TableHead> : null}
                        <TableHead>Campaign</TableHead>
                        <TableHead>In-group</TableHead>
                        <TableHead>Wait</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(waiting.calls || []).map((c) => (
                        <TableRow key={c.call_id}>
                          <TableCell className="tabular-nums">{nn(c.phone)}</TableCell>
                          {opts.showCustInfo ? <TableCell>{nn(c.customer_name)}</TableCell> : null}
                          <TableCell>{nn(c.campaign_name)}</TableCell>
                          <TableCell>{nn(c.inbound_group)}</TableCell>
                          <TableCell className="tabular-nums">{fmtDur(c.queued_sec)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          <div className="mb-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="live-stats-agent-counts">
            <Kpi label="Agents logged in" value={counts.logged_in || 0} />
            <Kpi label="Agents in calls" value={counts.in_calls || 0} />
            <Kpi label="Agents waiting" value={counts.waiting || 0} />
            <Kpi label="Paused agents" value={counts.paused || 0} />
            <Kpi label="Agents in dead calls" value={counts.dead_calls || 0} />
            <Kpi label="Agents in dispo" value={counts.dispo || 0} />
          </div>

          {!opts.summary && (
            <div className="mb-5 rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="live-stats-agents">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="text-sm font-semibold">Agent time on calls</h2>
                <p className="text-xs text-muted-foreground">Campaign: {campaignId ? (board?.campaigns || []).find((c) => c.id === campaignId)?.name || campaignId : "ALL-ACTIVE"}</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Station</TableHead>
                    <TableHead>User</TableHead>
                    {opts.viewUserGroup ? <TableHead>User group</TableHead> : null}
                    <TableHead>ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead>Hold</TableHead>
                    <TableHead>In-group</TableHead>
                    {opts.showCustInfo ? <TableHead>Customer</TableHead> : null}
                    {opts.showServerInfo ? <TableHead>Server</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-sm text-muted-foreground">No agents logged in.</TableCell>
                    </TableRow>
                  ) : agents.map((a) => (
                    <TableRow key={a.session_id} data-testid={`live-stats-agent-${a.session_id}`}>
                      <TableCell className="font-mono text-xs">{nn(a.station)}</TableCell>
                      <TableCell className="font-medium">{nn(a.user)}</TableCell>
                      {opts.viewUserGroup ? <TableCell className="text-xs">{nn(a.user_group)}</TableCell> : null}
                      <TableCell className="font-mono text-xs">{nn(a.extension)}</TableCell>
                      <TableCell>
                        <span
                          className="inline-flex min-w-[5.5rem] items-center justify-center rounded px-2 py-0.5 text-[11px] font-bold text-white"
                          style={{ background: agentBarColor(a) }}
                          data-testid="live-stats-agent-status"
                        >
                          {a.status}
                        </span>
                      </TableCell>
                      <TableCell className="tabular-nums font-mono text-xs">{fmtDur(a.elapsed_sec)}</TableCell>
                      <TableCell className="text-xs">{nn(a.campaign)}</TableCell>
                      <TableCell className="text-right tabular-nums">{a.calls ?? 0}</TableCell>
                      <TableCell className="tabular-nums text-xs">{fmtDur(a.hold_sec)}</TableCell>
                      <TableCell className="text-xs">{(a.inbound_groups || []).join(", ") || "—"}</TableCell>
                      {opts.showCustInfo ? <TableCell className="text-xs">{nn(a.customer_name || a.phone)}</TableCell> : null}
                      {opts.showServerInfo ? <TableCell className="font-mono text-xs">{nn(a.media_host)}</TableCell> : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {opts.showInGroupStats && (
            <div className="mb-5 rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="live-stats-ingroups">
              <div className="px-4 py-3 border-b border-border text-sm font-semibold">In-group stats</div>
              {(board?.ingroups || []).length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted-foreground">No inbound groups on these campaigns.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>In-group</TableHead>
                      <TableHead className="text-right">Waiting</TableHead>
                      <TableHead className="text-right">Agents</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(board?.ingroups || []).map((g) => (
                      <TableRow key={g.id}>
                        <TableCell>{g.name}</TableCell>
                        <TableCell className="text-right tabular-nums">{g.waiting}</TableCell>
                        <TableCell className="text-right tabular-nums">{g.agents}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {opts.showPhones && (
            <div className="mb-5 rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="live-stats-phones">
              <div className="px-4 py-3 border-b border-border text-sm font-semibold">Phones</div>
              {(board?.phones || []).length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted-foreground">No agent phones registered.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SIP user</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Campaign</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(board?.phones || []).map((p) => (
                      <TableRow key={p.sip_username}>
                        <TableCell className="font-mono text-xs">{p.sip_username}</TableCell>
                        <TableCell>{p.user}</TableCell>
                        <TableCell>{p.status}</TableCell>
                        <TableCell>{nn(p.campaign)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {opts.showServerInfo && (
            <div className="mb-5 rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="live-stats-servers">
              <div className="px-4 py-3 border-b border-border text-sm font-semibold">Server info</div>
              {(board?.servers || []).length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted-foreground">No media servers on live agent sessions.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Host</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Agents</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(board?.servers || []).map((s) => (
                      <TableRow key={s.host}>
                        <TableCell className="font-mono text-xs">{s.host}</TableCell>
                        <TableCell>{s.type}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.agents}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {opts.showMonitors && (
            <div className="mb-5 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground" data-testid="live-stats-monitors">
              No active listen / barge / whisper monitors.
            </div>
          )}

          <p className="mb-2 text-xs text-muted-foreground" data-testid="live-stats-system">
            {board?.system?.agents_logged_in || 0} agents logged in
            {board?.system?.pd_live ? ` · ${board.system.pd_live} predictive live` : ""}
          </p>

          <div className="rounded-xl border border-border bg-card p-4" data-testid="live-stats-legend">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status colors</p>
            <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
              {LEGEND.map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-xs">
                  <span className="h-3 w-6 rounded-sm shrink-0" style={{ background: item.color }} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Dialog open={optionsOpen} onOpenChange={setOptionsOpen}>
        <DialogContent data-testid="live-stats-options-modal">
          <DialogHeader>
            <DialogTitle>Report display options</DialogTitle>
            <DialogDescription>Same toggles as the Real-Time Main Report. Changes apply immediately and stay in this tab.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {[
              ["viewMore", "View more campaign stats"],
              ["viewUserGroup", "View user group"],
              ["showServerInfo", "Show server info"],
              ["hideWaitingCalls", "Hide waiting calls"],
              ["showInGroupStats", "Show in-group stats"],
              ["showPhones", "Show phones"],
              ["showMonitors", "Show monitors"],
              ["showCustInfo", "Show customer info"],
              ["summary", "Summary only (hide agent table)"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!opts[key]}
                  onChange={() => toggle(key)}
                />
                {label}
              </label>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
